// Creating an account on the player web (spec/15 P-03; F-IAM-02 … F-IAM-05; F-CMP-06): with an email and a password,
// or with Google, Discord or Steam where this deployment has them on. Phone sign-up stays off (the Platform's switch).
//
// The email way: the name, the address and a password, with the terms and privacy in force agreed first
// (`/sign-up/email` with `consent`); then the code sent to the address (`/email-otp/verify-email`), and the page
// signs in with the same password. The answer is the same for an address that already has an account, so the
// page says nothing about which addresses exist. A provider button goes straight to the provider; one with no
// Clavian account comes back `?error=CONSENT_REQUIRED`, answered with "Create your Clavian account" (`social.tsx`).

import type { SocialProvider } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Checkbox, Skeleton } from '@clavian/ui-web';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';

import { useApi } from '../api/client';
import { queryKeys, useBrandLegal, useSocialProviders } from '../api/queries';
import { AuthLayout } from './AuthLayout';
import { useAuthFailure } from './auth-errors';
import { Failure, TextInput } from './form-controls';
import { ConsentLabel, CreateAccountDialog, isSocialProvider, SocialButtons, SocialDivider, socialRefusal, withQuery, type SocialRoundTrip } from './social';
import { SecondFactorForm, VerifyEmailForm } from './steps';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Step = { readonly kind: 'form' } | { readonly kind: 'verify'; readonly email: string; readonly password: string } | { readonly kind: 'second-factor' };

export function SignUpPage(): ReactNode {
  const { t } = useTranslation('player');
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = useSearch({ from: '/sign-up' });
  const { providers, isPending } = useSocialProviders();
  const back = search.redirect === undefined ? {} : { redirect: search.redirect };
  const refusal = socialRefusal(search.error);
  const returned = isSocialProvider(search.provider) ? search.provider : undefined;
  const [creating, setCreating] = useState<SocialProvider | null>(refusal === 'noAccount' && returned !== undefined ? returned : null);
  const [step, setStep] = useState<Step>({ kind: 'form' });

  const trip = (_provider: SocialProvider): SocialRoundTrip => ({
    landing: search.redirect ?? '/home',
    newUserLanding: withQuery('/welcome', back),
    onError: withQuery('/sign-up', back),
  });

  const signedIn = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.session });
    await queryClient.refetchQueries({ queryKey: queryKeys.session });
    await navigate({ to: search.redirect ?? '/home' });
  };

  const refusalMessage =
    refusal === undefined || (refusal === 'noAccount' && returned !== undefined)
      ? null
      : refusal === 'banned'
        ? t('signIn.failure.banned')
        : returned === undefined
          ? t(`signInError.error.${refusal}`)
          : t(`social.error.${refusal}`, { provider: t(`social.provider.${returned}`) });

  if (step.kind === 'verify') {
    return (
      <AuthLayout title={t('signIn.verify.title')}>
        <VerifyEmailForm
          email={step.email}
          onVerified={async () => {
            const result = await api.signInEmail({ body: { email: step.email, password: step.password } });
            if (result.twoFactorRedirect === true) setStep({ kind: 'second-factor' });
            else await signedIn();
          }}
        />
      </AuthLayout>
    );
  }
  if (step.kind === 'second-factor') {
    return (
      <AuthLayout title={t('signIn.secondFactor.title')}>
        <SecondFactorForm onSignedIn={signedIn} onBack={() => void navigate({ to: '/sign-in' })} />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('signUp.title')} subtitle={t('signUp.intro')} footer={t('signUp.footer')}>
      <Failure message={refusalMessage} />
      <EmailSignUpForm onCreated={(email, password) => setStep({ kind: 'verify', email, password })} />
      {isPending ? (
        <Skeleton shape="text" lines={2} />
      ) : providers.length === 0 ? null : (
        <>
          <SocialDivider />
          <SocialButtons providers={providers} trip={trip} />
        </>
      )}
      <p className="pw-auth__alt">
        <Link to="/sign-in" search={back}>
          {t('signUp.signInInstead')}
        </Link>
      </p>
      <CreateAccountDialog provider={creating} trip={trip} onClose={() => setCreating(null)} />
    </AuthLayout>
  );
}

/** The sign-up page's own wording for the better-auth codes it can tell apart; the rest read as the shared copy. */
const SIGN_UP_KEYS: Readonly<Record<string, string>> = {
  PASSWORD_COMPROMISED: 'signUp.failure.weak',
  PASSWORD_TOO_SHORT: 'signUp.failure.short',
  INVALID_EMAIL: 'signUp.failure.email',
  CONSENT_VERSION_OUTDATED: 'signUp.failure.outdated',
  CONSENT_REQUIRED: 'signUp.consent.required',
};

function EmailSignUpForm({ onCreated }: { readonly onCreated: (email: string, password: string) => void }): ReactNode {
  const { t } = useTranslation('player');
  const api = useApi();
  const failureOf = useAuthFailure();
  const { legal, isPending } = useBrandLegal();
  const [agreed, setAgreed] = useState(false);
  const [needsConsent, setNeedsConsent] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const form = useForm<{ name: string; email: string; password: string }>({ defaultValues: { name: '', email: '', password: '' } });

  if (isPending) return <Skeleton shape="text" lines={4} />;
  if (legal === undefined) {
    return (
      <p className="pw-alert" role="status">
        {t('signUp.legalUnavailable')}
      </p>
    );
  }

  const submit = form.handleSubmit(async ({ name, email, password }) => {
    setFailure(null);
    if (!agreed) {
      setNeedsConsent(true);
      return;
    }
    const address = email.trim();
    try {
      await api.signUpWithEmailAndPassword({
        body: {
          name: name.trim(),
          email: address,
          password,
          consent: { terms: legal.terms.version, privacy: legal.privacy.version, ...(marketing ? { marketing: true } : {}) },
        },
      });
      onCreated(address, password);
    } catch (error) {
      setFailure(failureOf(error, SIGN_UP_KEYS));
    }
  });

  return (
    <form className="pw-form" onSubmit={(event) => void submit(event)} noValidate>
      <Failure message={failure} />
      <TextInput
        control={form.control}
        name="name"
        label={t('signUp.form.name')}
        description={t('signUp.form.nameHint')}
        autoComplete="name"
        enterKeyHint="next"
        rules={{ required: t('signIn.required') }}
      />
      <TextInput
        control={form.control}
        name="email"
        label={t('signUp.form.email')}
        type="email"
        inputMode="email"
        autoComplete="email"
        enterKeyHint="next"
        rules={{ required: t('signIn.required'), validate: (value: string) => EMAIL.test(value.trim()) || t('signUp.failure.email') }}
      />
      <TextInput
        control={form.control}
        name="password"
        label={t('signUp.form.password')}
        description={t('signUp.form.passwordHint')}
        type="password"
        autoComplete="new-password"
        enterKeyHint="done"
        rules={{ required: t('signIn.required'), validate: (value: string) => value.length >= 8 || t('signUp.failure.short') }}
      />
      <Checkbox
        label={<ConsentLabel legal={legal} />}
        checked={agreed}
        onCheckedChange={(checked) => {
          setAgreed(checked === true);
          if (checked === true) setNeedsConsent(false);
        }}
        aria-invalid={needsConsent}
      />
      {needsConsent ? (
        <p className="pw-alert" role="alert">
          {t('signUp.consent.required')}
        </p>
      ) : null}
      <Checkbox label={t('signUp.consent.marketing')} checked={marketing} onCheckedChange={(checked) => setMarketing(checked === true)} />
      <Button type="submit" variant="primary" block busy={form.formState.isSubmitting}>
        {t('signUp.form.submit')}
      </Button>
    </form>
  );
}
